import Phaser from 'phaser';

/**
 * W5: ParallaxSystem — three-layer depth illusion behind game objects.
 *
 * Each layer is a placeholder tinted Rectangle that scrolls at a fraction of
 * the camera's X offset. W5 ships colored rectangles only (no image assets);
 * W6 may replace Rectangle with TileSprite + real texture key.
 *
 * Layer table:
 *   bg  — scrollFactor 0.3  — 800×600 dark tinted, depth -10
 *   mid — scrollFactor 0.6  — 800×0.25h mid-tone strip,  depth -9
 *   fg  — scrollFactor 1.0  — game objects (NOT created here)
 *
 * Usage:
 *   // create():
 *   this._parallax = new ParallaxSystem(this);
 *   this._parallax.create(factionColor);
 *
 *   // update():
 *   this._parallax.update(this.cameras.main.scrollX);
 *
 *   // shutdown():
 *   this._parallax.destroy();
 *
 * setScrollFactor(0) pins the rectangle to screen space; update() then manually
 * repositions x by (scrollX * scrollFactor) to simulate depth. This avoids
 * Phaser's built-in scrollFactor on non-TileSprite objects (it behaves
 * unexpectedly when the camera follows a target).
 */
export interface ParallaxLayer {
  obj: Phaser.GameObjects.TileSprite | Phaser.GameObjects.Rectangle;
  scrollFactor: number;
}

export class ParallaxSystem {
  private layers: ParallaxLayer[] = [];

  constructor(private scene: Phaser.Scene) {}

  /**
   * Create the bg + mid layers. Call once in scene create().
   *
   * @param factionColor  Hex color for the bg layer tint (faction-themed sky).
   *                      Default 0x1a1a2e (dark navy, neutral).
   */
  create(factionColor: number = 0x1a1a2e): void {
    const width = this.scene.scale?.width ?? 800;
    const height = this.scene.scale?.height ?? 600;
    try {
      // Layer 0: BG — full-viewport faction-tinted floor at depth -12, BELOW the
      // stageBg tileSprite (-10). This guarantees the screen is never blank/grey:
      // when the PNG fails to load, this faction-colored fill shows instead of a
      // flat placeholder. Slightly wider than the viewport so the parallax slide
      // never reveals a hard edge.
      const bg = this.scene.add.rectangle(width / 2, height / 2, width * 1.4, height, factionColor)
        .setDepth(-12)
        .setScrollFactor(0);   // static — manually offset in update()
      this.layers.push({ obj: bg, scrollFactor: 0.2 });

      // NOTE (v1.1 battle-flow P0): the old mid-tone strip (0x2a2a4e at 60%
      // height) was the「中間灰色長方形」boss reported when the stage PNG failed
      // to load. Removed — the faction-tinted full bg above is the only parallax
      // floor now; the real stageBg tileSprite (drawn in create()/_spawnBoss)
      // provides the visual texture on top.
    } catch { /* headless test harness */ }
  }

  /**
   * Scroll the bg + mid layers by their respective fractions of camera offset.
   * Call every update() frame before game-object updates.
   *
   * @param cameraScrollX  this.cameras.main.scrollX from the owning scene.
   */
  update(cameraScrollX: number): void {
    for (const layer of this.layers) {
      try {
        // Objects with scrollFactor 0.3 move 30% as fast as the camera → far away.
        (layer.obj as { x: number }).x = cameraScrollX * layer.scrollFactor;
      } catch { /* headless */ }
    }
  }

  /**
   * Destroy all layer game objects. Call in scene shutdown/destroy.
   */
  destroy(): void {
    for (const layer of this.layers) {
      try { layer.obj.destroy(); } catch { /* headless */ }
    }
    this.layers = [];
  }
}
